import { useCallback } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import IntakeFormService, {
  CompleteOncUppGisTriageResponse,
  ConfirmOncUppGisTriageBookingResponse,
  GetActiveOncUppGisTriageResponse,
  MarkOncUppGisTriageIntakeStartedResponse,
  OncUppGisTriageData,
  UpdateOncUppGisTriageStepResponse,
  UpdatePrefilledDataInput,
} from "@app/service/intakeForm";
import { useGisGuest } from "./context";

interface GetActiveArgs {
  orderUidPublic: string;
  testUid: string;
}

interface UpdateStepArgs {
  sessionId: number;
  input: Record<string, unknown>;
  confirmedPhone?: string;
  confirmedEmail?: string;
  prefilledInput?: UpdatePrefilledDataInput;
}

interface SessionIdArgs {
  sessionId: number;
}

/**
 * Result of the GIS session operations, abstracting authenticated vs guest
 * (token-authorized) GraphQL. Each returns the resolved session (or null when
 * none is active) so callers can read `id`, `triageResult`, etc.
 */
export interface GisSessionOps {
  getActiveSession: (
    args: GetActiveArgs
  ) => Promise<OncUppGisTriageData | null>;
  updateStep: (args: UpdateStepArgs) => Promise<OncUppGisTriageData | null>;
  completeSession: (args: SessionIdArgs) => Promise<OncUppGisTriageData | null>;
  confirmBooking: (args: SessionIdArgs) => Promise<OncUppGisTriageData | null>;
  markIntakeStarted: (
    args: SessionIdArgs
  ) => Promise<OncUppGisTriageData | null>;
}

/**
 * Centralizes the GIS triage session GraphQL so the shared intake/schedule
 * components work for both the authenticated patient and the anonymous guest.
 *
 * When a guest `token` is present (via `GisGuestContext`), every call routes
 * through the NonAuth resolvers and threads the token; otherwise the existing
 * authenticated operations are used unchanged.
 */
export function useGisSessionOps(): GisSessionOps {
  const { token } = useGisGuest();

  const [fetchActiveAuth] = useLazyQuery<GetActiveOncUppGisTriageResponse>(
    IntakeFormService.getActiveOncUppGisTriage()
  );
  const [fetchActiveGuest] = useLazyQuery<{
    getActiveOncUppGisTriageNonAuth: OncUppGisTriageData | null;
  }>(IntakeFormService.getActiveOncUppGisTriageNonAuth());

  const [updateAuth] = useMutation<UpdateOncUppGisTriageStepResponse>(
    IntakeFormService.updateOncUppGisTriageStep()
  );
  const [updateGuest] = useMutation<{
    updateOncUppGisTriageStepNonAuth: OncUppGisTriageData;
  }>(IntakeFormService.updateOncUppGisTriageStepNonAuth());

  const [completeAuth] = useMutation<CompleteOncUppGisTriageResponse>(
    IntakeFormService.completeOncUppGisTriage()
  );
  const [completeGuest] = useMutation<{
    completeOncUppGisTriageNonAuth: OncUppGisTriageData;
  }>(IntakeFormService.completeOncUppGisTriageNonAuth());

  const [confirmAuth] = useMutation<ConfirmOncUppGisTriageBookingResponse>(
    IntakeFormService.confirmOncUppGisTriageBooking()
  );
  const [confirmGuest] = useMutation<{
    confirmOncUppGisTriageBookingNonAuth: OncUppGisTriageData;
  }>(IntakeFormService.confirmOncUppGisTriageBookingNonAuth());

  const [markStartedAuth] =
    useMutation<MarkOncUppGisTriageIntakeStartedResponse>(
      IntakeFormService.markOncUppGisTriageIntakeStarted()
    );
  const [markStartedGuest] = useMutation<{
    markOncUppGisTriageIntakeStartedNonAuth: OncUppGisTriageData;
  }>(IntakeFormService.markOncUppGisTriageIntakeStartedNonAuth());

  const getActiveSession = useCallback(
    async ({
      orderUidPublic,
      testUid,
    }: GetActiveArgs): Promise<OncUppGisTriageData | null> => {
      if (token) {
        const { data } = await fetchActiveGuest({
          variables: { token, orderUidPublic, testUid },
          fetchPolicy: "network-only",
        });
        return data?.getActiveOncUppGisTriageNonAuth ?? null;
      }
      const { data } = await fetchActiveAuth({
        variables: { orderUidPublic, testUid },
        fetchPolicy: "network-only",
      });
      return data?.getActiveOncUppGisTriage ?? null;
    },
    [token, fetchActiveAuth, fetchActiveGuest]
  );

  const updateStep = useCallback(
    async ({
      sessionId,
      input,
      confirmedPhone,
      confirmedEmail,
      prefilledInput,
    }: UpdateStepArgs): Promise<OncUppGisTriageData | null> => {
      if (token) {
        const { data } = await updateGuest({
          variables: {
            token,
            sessionId,
            input,
            confirmedPhone,
            confirmedEmail,
            prefilledInput,
          },
        });
        return data?.updateOncUppGisTriageStepNonAuth ?? null;
      }
      const { data } = await updateAuth({
        variables: {
          sessionId,
          input,
          confirmedPhone,
          confirmedEmail,
          prefilledInput,
        },
      });
      return data?.updateOncUppGisTriageStep ?? null;
    },
    [token, updateAuth, updateGuest]
  );

  const completeSession = useCallback(
    async ({
      sessionId,
    }: SessionIdArgs): Promise<OncUppGisTriageData | null> => {
      if (token) {
        const { data } = await completeGuest({
          variables: { token, sessionId },
        });
        return data?.completeOncUppGisTriageNonAuth ?? null;
      }
      const { data } = await completeAuth({ variables: { sessionId } });
      return data?.completeOncUppGisTriage ?? null;
    },
    [token, completeAuth, completeGuest]
  );

  const confirmBooking = useCallback(
    async ({
      sessionId,
    }: SessionIdArgs): Promise<OncUppGisTriageData | null> => {
      if (token) {
        const { data } = await confirmGuest({
          variables: { token, sessionId },
        });
        return data?.confirmOncUppGisTriageBookingNonAuth ?? null;
      }
      const { data } = await confirmAuth({ variables: { sessionId } });
      return data?.confirmOncUppGisTriageBooking ?? null;
    },
    [token, confirmAuth, confirmGuest]
  );

  const markIntakeStarted = useCallback(
    async ({
      sessionId,
    }: SessionIdArgs): Promise<OncUppGisTriageData | null> => {
      if (token) {
        const { data } = await markStartedGuest({
          variables: { token, sessionId },
        });
        return data?.markOncUppGisTriageIntakeStartedNonAuth ?? null;
      }
      const { data } = await markStartedAuth({ variables: { sessionId } });
      return data?.markOncUppGisTriageIntakeStarted ?? null;
    },
    [token, markStartedAuth, markStartedGuest]
  );

  return {
    getActiveSession,
    updateStep,
    completeSession,
    confirmBooking,
    markIntakeStarted,
  };
}
